import { useContext, useSyncExternalStore } from 'react';

import { isNotDefined } from '@enterprise/core';

import type { PaginationContextState } from '#pagination/context/types/context.type';

import { PaginationContext } from '#pagination/context/utilities/pagination.context';

/**
 * Custom hook to select a specific piece of state from the PaginationContext using a selector function.
 *
 * This hook leverages React's `useSyncExternalStore` to subscribe to changes in the pagination context,
 * ensuring the selected state is always up-to-date and triggers re-renders when necessary.
 *
 * @template SelectorOutput - The type of the value returned by the selector function.
 * @param selector - A function that receives the current PaginationContextState and returns the desired piece of state.
 * @returns The selected piece of state from the PaginationContext.
 * @throws Error if the PaginationContext store is not found.
 */
export const usePaginationSelector = <SelectorOutput>(selector: (store: PaginationContextState) => SelectorOutput) => {
  const store = useContext(PaginationContext);

  if (isNotDefined(store)) {
    throw new Error('Store not found');
  }

  return useSyncExternalStore(store.subscribe, () => {
    return selector(store.get());
  });
};
